import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { Store } from '@ngrx/store';
import { DocumentUpdate } from '@completion/actions';
import { ProcedureDocument } from '@completion/models';
import { DocumentNode } from '@completion/models';

@Injectable({ providedIn: 'root' })
export class DocumentUpdateQueueService {
  private update$ = new Subject<any>();
  private lastEmitted: any = null;
  private lastQueued: any = null;

  constructor(private store: Store) {
    // debounce burst updates and dedupe identical payloads
    const tracingEnabled =
      typeof window !== 'undefined' &&
      !!(window.location && window.location.search && window.location.search.indexOf('docqueueTrace=true') >= 0);
    this.update$.pipe(debounceTime(250)).subscribe((doc) => {
      if (tracingEnabled) {
        console.trace('DocumentUpdateQueueService.emit stack');
      }
      if (isEqual(this.lastEmitted, doc)) {
        console.log('DocumentUpdateQueueService: skipping identical payload');
        return;
      }
      this.lastEmitted = doc;
      this.store.dispatch(new DocumentUpdate(doc));
    });
  }

  private assignOrdinals(doc: ProcedureDocument): void {
    const assign = (nodes: DocumentNode[] | undefined) => {
      if (!nodes) return;
      nodes.forEach((n, i) => {
        n.ordinal = i;
        if (n.children && n.children.length) assign(n.children);
      });
    };
    assign(doc.nodes);
  }

  /** True if a queued (debounced) update has not yet been emitted */
  isPending(): boolean {
    return this.lastQueued && !isEqual(this.lastQueued, this.lastEmitted);
  }

  /** Immediately dispatch the latest queued update (if any) bypassing debounce */
  flushLatest(): void {
    if (this.isPending()) {
      this.lastEmitted = this.lastQueued;
      this.store.dispatch(new DocumentUpdate(this.lastQueued));
    }
  }

  /** Return the last queued (but maybe not emitted) document clone */
  getLastQueued(): any {
    return this.lastQueued;
  }

  queue(doc: ProcedureDocument) {
    const tracingEnabled =
      typeof window !== 'undefined' &&
      !!(window.location && window.location.search && window.location.search.indexOf('docqueueTrace=true') >= 0);
    if (tracingEnabled) {
      console.trace('DocumentUpdateQueueService.queue stack');
    }
    const cloned = cloneDeep(doc);

    try {
      this.assignOrdinals(cloned as ProcedureDocument);
    } catch (e) {
      console.warn('Ordinal assignment failed', e);
    }

    if (isEqual(this.lastQueued, cloned)) {
      return;
    }
    this.lastQueued = cloned;
    this.update$.next(cloned);
  }

  /** Immediately dispatch a document update bypassing debounce — useful for drag-drop reorders */
  queueImmediate(doc: ProcedureDocument) {
    const tracingEnabled =
      typeof window !== 'undefined' &&
      !!(window.location && window.location.search && window.location.search.indexOf('docqueueTrace=true') >= 0);
    if (tracingEnabled) {
      console.trace('DocumentUpdateQueueService.queueImmediate stack');
    }
    const cloned = cloneDeep(doc);
    try {
      this.assignOrdinals(cloned as ProcedureDocument);
    } catch (e) {
      console.warn('Ordinal assignment failed', e);
    }
    // If the payload is identical to the last emitted one, skip dispatch
    if (isEqual(this.lastEmitted, cloned)) return;
    this.lastQueued = cloned;
    this.lastEmitted = cloned;
    this.store.dispatch(new DocumentUpdate(cloned));
  }
}
